// src/SessionDetail.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "./supabaseClient";

// human-friendly time formatter
function formatElapsed(seconds) {
  if (!Number.isFinite(seconds)) return "0s";
  const total = Math.max(0, Math.floor(seconds));
  const units = [
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
    ["s", 1],
  ];

  let remaining = total;
  const parts = [];

  for (const [label, unitSeconds] of units) {
    if (remaining >= unitSeconds) {
      const value = Math.floor(remaining / unitSeconds);
      remaining %= unitSeconds;
      parts.push(`${value}${label}`);
    }
    if (parts.length === 2) break;
  }

  if (!parts.length) return "0s";
  return parts.join(" ");
}

export default function SessionDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [devMessageText, setDevMessageText] = useState("");
  const [sendingDevMessage, setSendingDevMessage] = useState(false);

  // Recording state
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(null);

  const [selectedEventId, setSelectedEventId] = useState(null);

  // Duration we *measure* in the browser
  const [videoDurationSeconds, setVideoDurationSeconds] = useState(null);

  const videoRef = useRef(null);
  const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

  // Screenshot object URLs, keyed by event.id
  const [screenshotUrls, setScreenshotUrls] = useState({}); // { [eventId]: objectUrl }
  const inflightRef = useRef(new Set()); // prevent duplicate fetches

  // Helper: fetch screenshot bytes via edge function (auth’d) and return blob
  async function fetchScreenshotBlob(key) {
    if (!SUPABASE_URL) throw new Error("Missing REACT_APP_SUPABASE_URL");
    const { data: authData, error: authErr } = await supabase.auth.getSession();
    if (authErr) console.warn("auth.getSession error:", authErr);
    const accessToken = authData?.session?.access_token || null;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-screenshot`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ key }),
    });

    if (!res.ok) throw new Error(`get-screenshot failed ${res.status}`);
    return await res.blob();
  }

  // Load session + events
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: s, error: sErr } = await supabase
          .from("sessions")
          .select("*")
          .eq("id", sessionId)
          .single();
        if (sErr) throw sErr;
        if (!cancelled) setSession(s);

        const { data: evData, error: evErr } = await supabase
          .from("session_events")
          .select("*")
          .eq("session_id", sessionId)
          .order("created_at", { ascending: true });
        if (evErr) throw evErr;
        if (!cancelled) setEvents(evData || []);
      } catch (e) {
        console.error("Failed to load session detail:", e);
        if (!cancelled) setError("Failed to load this session.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Live updates for events
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`session-events-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "session_events",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const ev = payload.new;
          setEvents((prev) => [...prev, ev]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  // Load screenshots for any events that have screenshot_key
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!events || events.length === 0) return;

      const withShots = events.filter((ev) => ev?.payload?.screenshot_key);
      for (const ev of withShots) {
        if (cancelled) return;
        if (!ev || !ev.id) continue;

        if (screenshotUrls[ev.id]) continue;

        const key = ev.payload.screenshot_key;
        if (!key || typeof key !== "string") continue;

        // Deduplicate inflight
        const inflight = inflightRef.current;
        if (inflight.has(ev.id)) continue;
        inflight.add(ev.id);

        try {
          const blob = await fetchScreenshotBlob(key);
          if (cancelled) return;

          const url = URL.createObjectURL(blob);
          setScreenshotUrls((prev) => ({ ...prev, [ev.id]: url }));
        } catch (e) {
          console.warn("Failed to load screenshot for event", ev.id, e);
        } finally {
          inflight.delete(ev.id);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Cleanup screenshot object URLs on unmount or when replaced
  useEffect(() => {
    return () => {
      try {
        Object.values(screenshotUrls).forEach((u) => {
          try {
            URL.revokeObjectURL(u);
          } catch (_) {}
        });
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load recording ONLY after session is ended
  useEffect(() => {
    if (!sessionId || !session || !session.ended_at) return;
    if (!SUPABASE_URL) {
      console.error("Missing REACT_APP_SUPABASE_URL env var");
      setRecordingError("Recording endpoint not configured.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setRecordingLoading(true);
        setRecordingError(null);

        const { data: authData, error: authErr } =
          await supabase.auth.getSession();
        if (authErr) {
          console.warn("Failed to get auth session for recording:", authErr);
        }
        const accessToken = authData?.session?.access_token || null;

        // Edge function that concatenates chunks into a single WebM
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/get-session-recording`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
            body: JSON.stringify({ session_id: sessionId }),
          }
        );

        if (!res.ok) {
          console.warn("get-session-recording failed:", res.status);
          if (!cancelled) {
            setRecordingError("No recording available for this session.");
          }
          return;
        }

        const blob = await res.blob();
        if (!blob || blob.size === 0) {
          if (!cancelled) {
            setRecordingError("Recording file is empty.");
          }
          return;
        }

        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }

        // reset duration when we load a new file
        setVideoDurationSeconds(null);

        // Revoke old URL if any
        setRecordingUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e) {
        console.error("Failed to load session recording:", e);
        if (!cancelled) {
          setRecordingError("Failed to load recording.");
        }
      } finally {
        if (!cancelled) {
          setRecordingLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, session, SUPABASE_URL]);

  // Cleanup recording URL on unmount
  useEffect(() => {
    return () => {
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
      }
    };
  }, [recordingUrl]);

  // Seek helper (video timeline is in "recording seconds")
  const seekTo = (seconds, eventId) => {
    const video = videoRef.current;
    if (!video || typeof seconds !== "number") return;
    try {
      video.currentTime = seconds;
      video.play().catch(() => {});
      if (eventId) setSelectedEventId(eventId);
    } catch (e) {
      console.warn("Seek failed:", e);
    }
  };

  // When the browser actually knows the duration, save it both locally
  // and (if needed) back into Supabase.
  const handleLoadedMetadata = async () => {
    const video = videoRef.current;
    if (!video) return;

    const d = video.duration;
    if (!Number.isFinite(d) || d <= 0) return;

    const rounded = Math.round(d);
    setVideoDurationSeconds(rounded);

    if (!session) return;

    const existing = session.recording_duration_seconds;
    if (existing == null || Math.abs(existing - rounded) > 1) {
      try {
        const { error: updErr } = await supabase
          .from("sessions")
          .update({ recording_duration_seconds: rounded })
          .eq("id", session.id);

        if (updErr) {
          console.warn("Failed to update recording_duration_seconds:", updErr);
        } else {
          setSession((prev) =>
            prev ? { ...prev, recording_duration_seconds: rounded } : prev
          );
        }
      } catch (e) {
        console.warn("Unexpected error updating recording_duration_seconds:", e);
      }
    }
  };

  const sendDevMessage = async () => {
    if (!session || !devMessageText.trim()) return;
    if (session.ended_at) {
      alert("This session has already been marked as ended.");
      return;
    }

    setSendingDevMessage(true);
    try {
      // NOTE: dev messages are timestamped against session.started_at
      const startedMs = new Date(session.started_at).getTime();
      const elapsedSeconds = Math.floor((Date.now() - startedMs) / 1000);

      const row = {
        session_id: session.id,
        direction: "dev",
        type: "message",
        note: devMessageText.trim(),
        payload: {
          tag: "Dev message",
          time_seconds: elapsedSeconds,
          screenshot_key: null,
          screenshot_content_type: null,
          screenshot_size_bytes: null,
        },
      };

      const { error } = await supabase.from("session_events").insert(row);
      if (error) {
        console.error("Failed to send dev message:", error);
        alert("Failed to send message");
      } else {
        setDevMessageText("");
      }
    } catch (e) {
      console.error("Unexpected error sending dev message:", e);
      alert("Failed to send message");
    } finally {
      setSendingDevMessage(false);
    }
  };

  const markSessionEnded = async () => {
    if (!session || session.ended_at) return;
    try {
      const { data, error } = await supabase
        .from("sessions")
        .update({ ended_at: new Date().toISOString() })
        .eq("id", session.id)
        .select()
        .single();
      if (error) throw error;
      setSession(data);
    } catch (e) {
      console.error("Failed to mark session ended:", e);
      alert("Failed to update session.");
    }
  };

  const lastEvent = events.length ? events[events.length - 1] : null;
  const lastEventTime =
    lastEvent && lastEvent.created_at
      ? new Date(lastEvent.created_at).toLocaleString()
      : null;

  return (
    <div
      className="
        h-[calc(100vh-2rem)] md:h-[calc(100vh-4rem)]
        flex flex-col
        text-[#eee] font-nunito
        overflow-hidden
      "
    >
      {/* Top bar: back + status / end button */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#888]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="underline text-[#93c5fd] decoration-[#93c5fd]/60"
          >
            ← Back
          </button>
        </div>

        {session && (
          <div className="flex items-center gap-2">
            <button
              onClick={markSessionEnded}
              disabled={!session || !!session.ended_at}
              className="text-xs px-3 py-1.5 rounded-lg border border-[#f97316] text-[#fed7aa] hover:bg-[#451a03] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {session?.ended_at ? "Session ended" : "Mark session as ended"}
            </button>
          </div>
        )}
      </div>

      {/* Loading / error states */}
      {loading && (
        <div className="flex-1 flex items-center justify-center text-sm text-[#aaa]">
          Loading session…
        </div>
      )}
      {!loading && error && (
        <div className="flex-1 flex items-center justify-center text-sm text-red-400">
          {error}
        </div>
      )}

      {!loading && !error && session && (
        <div className="flex flex-1 min-h-0 gap-4">
          {/* LEFT: big video + meta underneath (YouTube style) */}
          <div className="flex-[3] min-w-0 flex flex-col bg-[#151515] border border-[#252525] rounded-xl overflow-hidden">
            {/* Header above video */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#252525]">
              <div className="text-sm font-semibold">Session recording</div>
              {recordingLoading && (
                <span className="text-[11px] text-[#888]">Loading…</span>
              )}
            </div>

            {/* Video area */}
            <div className="flex-1 min-h-0 flex items-center justify-center bg-black">
              {!session.ended_at && (
                <div className="px-4 text-xs text-[#777] text-center">
                  Recording will be available after this session ends.
                </div>
              )}

              {session.ended_at && recordingError && (
                <div className="px-4 text-xs text-red-400 text-center">
                  {recordingError}
                </div>
              )}

              {session.ended_at && !recordingError && (
                <video
                  ref={videoRef}
                  src={recordingUrl || undefined}
                  controls
                  onLoadedMetadata={handleLoadedMetadata}
                  className="w-full h-full object-contain bg-black"
                />
              )}
            </div>

            {/* Meta section BELOW the video */}
            <div className="px-3 py-3 border-t border-[#252525] text-xs text-[#bbb] space-y-1">
              <div className="flex flex-row items-center gap-x-3 text-sm font-semibold text-[#eee]">
                Session: {sessionId || ""}
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    session.ended_at
                      ? "bg-red-900/40 text-red-300"
                      : "bg-green-900/40 text-green-300"
                  }`}
                >
                  {session.ended_at ? "Ended" : "Active"}
                </span>
              </div>
              <div>
                <span className="font-semibold text-[#eee]">Build ID:</span>{" "}
                {session.build_id || "Unknown"}
              </div>
              <div>
                <span className="font-semibold text-[#eee]">Tester:</span>{" "}
                {session.tester_email || "Unknown"}
              </div>
              <div>
                <span className="font-semibold text-[#eee]">Started:</span>{" "}
                {new Date(session.started_at).toLocaleString()}
              </div>
              {session.ended_at && (
                <div>
                  <span className="font-semibold text-[#eee]">Ended:</span>{" "}
                  {new Date(session.ended_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: dev message + feedback list */}
          <div className="flex-[2] min-w-[260px] max-w-sm md:max-w-md flex flex-col bg-[#151515] border border-[#252525] rounded-xl overflow-hidden">
            {/* Dev message composer */}
            <div className="p-3 border-b border-[#252525]">
              <div className="text-sm font-semibold mb-2">
                Send message to tester
              </div>
              <textarea
                value={devMessageText}
                onChange={(e) => setDevMessageText(e.target.value)}
                rows={4}
                className="w-full text-xs bg-[#111] border border-[#444] rounded p-2 text-[#eee] resize-none"
                placeholder={
                  session.ended_at
                    ? "This session has ended."
                    : "Type a short hint, question, or instruction…"
                }
                disabled={!!session.ended_at || sendingDevMessage}
              />
              <button
                onClick={sendDevMessage}
                disabled={
                  sendingDevMessage ||
                  !!session?.ended_at ||
                  !devMessageText.trim()
                }
                className="mt-2 inline-flex items-center justify-center px-3 py-1.5 rounded bg-[#8338ec] text-xs font-medium hover:bg-[#a855f7] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sendingDevMessage ? "Sending…" : "Send to tester"}
              </button>
            </div>

            {/* Feedback events list */}
            <div className="flex-1 min-h-0 p-3 overflow-y-auto">
              <div className="flex flex-col gap-y-2 mb-2">
                <div className="text-sm font-semibold">Feedback events</div>
                <div className="text-xs opacity-80">
                  Click an event to jump to it on the video.
                </div>
              </div>

              {events.length === 0 ? (
                <div className="text-xs text-[#777]">
                  No events yet for this session.
                </div>
              ) : (
                <ul className="space-y-2 text-xs">
                  {events.map((ev) => {
                    const isDev = ev.direction === "dev";
                    const meta = ev.payload || {};
                    const label = isDev ? "From you" : meta.tag || "Event";

                    const rawSeconds =
                      typeof meta.time_seconds === "number"
                        ? Math.max(0, meta.time_seconds)
                        : null;

                    const screenshotUrl = screenshotUrls[ev.id] || null;
                    const isSelected = ev.id === selectedEventId;

                    return (
                      <li
                        key={ev.id}
                        className={`p-2 rounded border cursor-pointer ${
                          isDev
                            ? "bg-[#1f1335] border-[#7c3aed]"
                            : "bg-[#161616] border-[#333]"
                        } ${isSelected ? "ring-1 ring-yellow-400" : ""}`}
                        onClick={() => {
                          if (rawSeconds != null) {
                            seekTo(rawSeconds, ev.id);
                          }
                        }}
                      >
                        <div className="flex justify-between mb-1">
                          <span className="font-semibold">{label}</span>
                          <span className="opacity-70">
                            {rawSeconds != null ? formatElapsed(rawSeconds) : ""}
                          </span>
                        </div>

                        {ev.note && (
                          <div className="whitespace-pre-wrap">{ev.note}</div>
                        )}

                        {meta.screenshot_key && !screenshotUrl && (
                          <div className="mt-1 text-[11px] text-[#888]">
                            Loading screenshot…
                          </div>
                        )}

                        {screenshotUrl && (
                          <div className="mt-1">
                            <img
                              src={screenshotUrl}
                              alt="Session screenshot"
                              className="w-24 h-24 object-cover rounded border border-[#444]"
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
